// @ts-nocheck
import ArrowBackward from "@store/Library/Components/Icons/ArrowBackward";
import FieldDefinition, { Option } from "@insite/client-framework/Types/FieldDefinition";
import { TabDefinition } from "@insite/client-framework/Types/TabDefinition";
import ChevronLeft from "@insite/mobius/Icons/ChevronLeft";
import ChevronRight from "@insite/mobius/Icons/ChevronRight";
import { getContextualCssUnitField } from "@store/Framework/Common/Utilities/FieldHelpers";
import { CarouselStyles } from "@store/Overrides/Components/Carousel";

import { carouselButtonStyle } from "@store/Themes/common/props/button";
import colors from "@store/Themes/common/styleguide/config/colors";
import ArrowForward from "@toolkit/Library/Components/Icons/ArrowForward";
import { css } from "styled-components";

export enum carouselFields {
    slideInterval = "slideInterval",
    arrowType = "arrowType",
    arrowColor = "arrowColor",
    arrowBackgroundColor = "arrowBackgroundColor",
    arrowHoverColor = "arrowHoverColor",
    arrowHoverBackgroundColor = "arrowHoverBackgroundColor",
    dotsColorSelected = "dotsColorSelected",
    dotsBackgroundColor = "dotsBackgroundColor",
    dotSize = "dotSize",
    dotsColorNotSelected = "dotsColorNotSelected",
    dotGap = "dotGap",
    dotBorderColor = "dotBorderColor",
    arrowInset = "arrowInset",
    dotInset = "dotInset",
    dotPadding = "dotPadding",
    arrowSize = "arrowSize",
    showArrows = "showArrows",
    dotHoverColor = "dotHoverColor",
    showDots = "showDots",
}

export interface CarouselFieldProps extends Record<carouselFields, number | string | boolean> {
    [carouselFields.arrowType]: ArrowType;
    [carouselFields.slideInterval]: number;
    [carouselFields.dotSize]: string;
    [carouselFields.arrowColor]: string;
    [carouselFields.arrowBackgroundColor]: string;
    [carouselFields.arrowHoverColor]: string;
    [carouselFields.arrowHoverBackgroundColor]: string;
    [carouselFields.dotsColorSelected]: string;
    [carouselFields.dotsColorNotSelected]: string;
    [carouselFields.dotsBackgroundColor]: string;
    [carouselFields.dotSize]: string;
    [carouselFields.dotGap]: string;
    [carouselFields.dotBorderColor]: string;
    [carouselFields.arrowInset]: string;
    [carouselFields.dotInset]: string;
    [carouselFields.dotPadding]: string;
    [carouselFields.showDots]: boolean;
    [carouselFields.arrowSize]: string;
    [carouselFields.dotHoverColor]: string;
    [carouselFields.showArrows]: boolean;
}

const dotSizeField = getContextualCssUnitField(carouselFields.dotSize);
const dotGapField = getContextualCssUnitField(carouselFields.dotGap);
const dotInsetField = getContextualCssUnitField(carouselFields.dotInset);
const dotPaddingField = getContextualCssUnitField(carouselFields.dotPadding);
const arrowInsetField = getContextualCssUnitField(carouselFields.arrowInset);
const arrowSizeField = getContextualCssUnitField(carouselFields.arrowSize);
type ArrowType = "none" | "chevron" | "arrow";
type IconProps = { position: "left" | "right"; src: React.NamedExoticComponent };

const arrowOptions: Option<ArrowType>[] = [
    { displayName: "None", value: "none" },
    { displayName: "Chevron", value: "chevron" },
    { displayName: "Arrow", value: "arrow" },
];

const dotTab: TabDefinition = {
    displayName: "Dots",
    sortOrder: 2,
};
const arrowTab: TabDefinition = {
    displayName: "Arrows",
    sortOrder: 1,
};

export const getCarouselButtonStyles = (
    direction: "left" | "right",
    arrowBackgroundColor: string,
    arrowColor: string,
    arrowHoverBackgroundColor: string,
    arrowHoverColor: string,
    arrowInset: string,
    arrowSize: string,
    showArrows: boolean,
) => {
    return css`
        position: relative !important;
        ${carouselButtonStyle(direction).css}
        width: ${arrowSize};
        height: ${arrowSize};
        svg {
            height: ${arrowSize} !important;
            width: ${arrowSize} !important;
            color: ${arrowColor};
        }
        background-color: ${arrowBackgroundColor || "transparent"};
        &:not(:hover) {
            ${arrowBackgroundColor ? "" : "box-shadow: none;"}
        }

        ${direction}: ${arrowInset} !important;
        &:hover {
            svg {
                color: ${arrowHoverColor};
            }
            background-color: ${arrowHoverBackgroundColor || "transparent"};
            ${arrowHoverBackgroundColor ? "" : "box-shadow: none;"}
        }
        ${!showArrows && "display: none;"}
    `;
};

export const getDotButtonStyles = (dotSize: string, dotBorderColor: string, dotColor: string, dotHoverColor: string) =>
    css`
        background-color: ${dotColor};
        width: ${dotSize};
        height: ${dotSize};
        margin-left: 0;

        ${dotBorderColor && `border: 1px solid ${dotBorderColor};`}
        &:hover {
            background-color: ${dotHoverColor};
        }
    `;

export const carouselFieldDefinitions: FieldDefinition[] = [
    {
        name: carouselFields.slideInterval,
        displayName: "Seconds per Slide",
        editorTemplate: "IntegerField",
        defaultValue: 0,
        fieldType: "General",
        max: 10,
        sortOrder: 2,
    },
    {
        name: carouselFields.arrowType,
        displayName: "Arrow Type",
        editorTemplate: "DropDownField",
        defaultValue: "arrow",
        fieldType: "General",
        sortOrder: 1,
        options: arrowOptions,
        tab: arrowTab,
    },

    {
        name: carouselFields.arrowColor,
        displayName: "Arrow Color",
        editorTemplate: "ColorPickerField",
        defaultValue: colors?.common?.background || "",
        fieldType: "General",
        sortOrder: 2,
        tab: arrowTab,
    },
    {
        name: carouselFields.arrowBackgroundColor,
        displayName: "Arrow Background Color",
        editorTemplate: "ColorPickerField",
        defaultValue: colors?.primary?.main || "",
        fieldType: "General",
        sortOrder: 3,
        tab: arrowTab,
    },
    {
        name: carouselFields.arrowHoverColor,
        displayName: "Arrow Hover Color",
        editorTemplate: "ColorPickerField",
        defaultValue: colors?.common?.background || "",
        fieldType: "General",
        sortOrder: 4,
        tab: arrowTab,
    },
    {
        ...arrowInsetField.definition,
        displayName: "Arrow Inset",
        defaultValue: "2rem",
        tab: arrowTab,
    },
    {
        name: carouselFields.arrowHoverBackgroundColor,
        displayName: "Arrow Hover Background Color",
        editorTemplate: "ColorPickerField",
        defaultValue: colors?.secondary?.main || "",
        fieldType: "General",
        sortOrder: 5,
        tab: arrowTab,
    },
    {
        displayName: "Arrow Size",
        ...arrowSizeField.definition,
        defaultValue: "2rem",
        tab: arrowTab,
    },
    {
        name: carouselFields.showArrows,
        displayName: "Show Arrows",
        editorTemplate: "CheckboxField",
        defaultValue: true,
        fieldType: "Contextual",
        sortOrder: 0,
        tab: arrowTab,
    },
    {
        name: carouselFields.dotsColorSelected,
        displayName: "Active Dots Color",
        editorTemplate: "ColorPickerField",
        defaultValue: colors?.primary?.main || "",
        fieldType: "General",
        sortOrder: 6,
        tab: dotTab,
    },
    {
        name: carouselFields.dotsColorNotSelected,
        displayName: "Inactive Dots Color",
        editorTemplate: "ColorPickerField",
        defaultValue: colors?.common?.accent || "",
        fieldType: "General",
        sortOrder: 6,
        tab: dotTab,
    },
    {
        ...dotInsetField.definition,
        displayName: "Dot Inset",
        defaultValue: "0rem",
        tab: dotTab,
    },
    {
        name: carouselFields.dotsBackgroundColor,
        displayName: "Dots Wrapper Color",
        editorTemplate: "ColorPickerField",
        defaultValue: "",
        fieldType: "General",
        sortOrder: 7,
        tab: dotTab,
    },
    {
        name: carouselFields.showDots,
        displayName: "Show Dots",
        editorTemplate: "CheckboxField",
        defaultValue: true,
        fieldType: "Contextual",
        sortOrder: 0,
        tab: dotTab,
    },
    {
        displayName: "Dot Size",
        ...dotSizeField.definition,
        defaultValue: "0.5rem",
        tab: dotTab,
    },
    {
        displayName: "Dot Gap",
        ...dotGapField.definition,
        defaultValue: "0.5rem",
        tab: dotTab,
    },
    {
        displayName: "Dot Border Color",
        name: carouselFields.dotBorderColor,
        editorTemplate: "ColorPickerField",
        defaultValue: "",
        fieldType: "General",
        sortOrder: 8,
        tab: dotTab,
    },
    {
        displayName: "Dot Hover Color",
        editorTemplate: "ColorPickerField",
        name: carouselFields.dotHoverColor,
        fieldType: "General",
        defaultValue: colors?.secondary?.main || "",
        tab: dotTab,
    },
    {
        displayName: "Dot Padding",
        ...dotPaddingField.definition,
        defaultValue: "0.5rem",
        tab: dotTab,
    },
];

export const getExtendedCarouselStyles = (fields: CarouselFieldProps, isEditing = false) => {
    const {
        arrowType,
        arrowBackgroundColor,
        arrowColor,
        arrowHoverBackgroundColor,
        arrowHoverColor,
        dotsBackgroundColor,
        dotsColorSelected,
        dotsColorNotSelected,
        dotSize,
        dotGap,
        dotBorderColor,
        arrowInset,
        dotInset,
        dotPadding,
        arrowSize,
        dotHoverColor,
        showArrows,
    } = fields;

    let extendedCarouselStyles: CarouselStyles = {};
    const nextIcon: IconProps =
        arrowType === "chevron" ? { src: ChevronRight, position: "right" } : { src: ArrowForward, position: "right" };
    const previousIcon: IconProps =
        arrowType === "chevron" ? { src: ChevronLeft, position: "left" } : { src: ArrowBackward, position: "left" };
    extendedCarouselStyles.nextArrowButton = {
        icon: nextIcon,
        css: getCarouselButtonStyles(
            "right",
            arrowBackgroundColor,
            arrowColor,
            arrowHoverBackgroundColor,
            arrowHoverColor,
            arrowInset,
            arrowSize,
            showArrows,
        ),
    };
    extendedCarouselStyles.prevArrowButton = {
        icon: previousIcon,
        css: getCarouselButtonStyles(
            "left",
            arrowBackgroundColor,
            arrowColor,
            arrowHoverBackgroundColor,
            arrowHoverColor,
            arrowInset,
            arrowSize,
            showArrows,
        ),
    };

    extendedCarouselStyles = {
        ...extendedCarouselStyles,
        carouselWrapper: {
            css: css`
                overflow: hidden;
            `,
        },
        dotsWrapper: {
            css: css`
                background-color: ${dotsBackgroundColor};
                width: 100%;
                column-gap: ${dotGap};
                display: flex;
                padding-block: 0;
                padding-inline: ${dotPadding};
                border-radius: 1rem;
                position: absolute;
                z-index: 999999;
                // This is a hack to make the dots appear in the correct place in the shell
                bottom: calc(${isEditing ? "4rem" : "1rem"} + ${dotInset});
            `,
        },
        dotButton: {
            css: getDotButtonStyles(dotSize, dotBorderColor, dotsColorNotSelected, dotHoverColor),
        },
        dotButtonSelected: {
            css: getDotButtonStyles(dotSize, dotBorderColor, dotsColorSelected, dotsColorSelected),
        },
    };
    return extendedCarouselStyles;
};
